/* eslint-disable react/prop-types */
import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  BookOpenText,
  Mail,
  ChevronLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  Store,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { TermsAndConditionsPopup } from '../../components/modals/TermsAndConditionsPopup';
import { PrivacyPolicyPopup } from '../../components/modals/PrivacyPolicyPopup';
import { usePWAInstall } from '../../hooks/usePwa';
import { getPWADisplayMode, isIOS, isStandalone } from '../../utils/PWAUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import emailjs from '@emailjs/browser';
import BiglocalLoader from '../../components/common/BigLocalLoader';
import { URLS } from '../../constant/constant';
import NotRegisteredModal from '../../components/modals/NotRegisteredModal';

// A new component for the initial Login/Register choice
const AuthChoice = ({ onLogin, onRegister }) => (
  <div className="w-full flex flex-col items-center animate-in fade-in duration-300 text-center">
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Welcome, Seller!</h2>
    <p className="text-gray-500 mt-2 mb-6 md:mb-8"></p>
    <div className="w-full max-w-sm space-y-4">
      <Button
        onClick={onLogin}
        className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700 rounded-full font-bold flex items-center justify-center"
      >
        {/* store icon */}
        <Store className="mr-2 h-5 w-5" />
        Existing Seller Login
      </Button>
      <Button
        onClick={onRegister}
        variant="outline"
        className="w-full h-12 text-base rounded-full font-bold text-blue-600 border-blue-600 hover:bg-blue-50"
      >
        New Seller? Register with us
      </Button>
    </div>
  </div>
);

export default function LoginPage() {
  const [phone, setPhone] = useState('');
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const { initiateLogin, initiateLoginByEmail, loading, setPhoneNumber, setEmailAddress, setLoginMethod, setIsModalOpen, isModalOpen } = useAuth();
  const [isContactUsOpen, setIsContactUsOpen] = useState(false);
  const [isIosModalOpen, setIsIosModalOpen] = useState(false);
  const { state } = useLocation();
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [phoneError, setPhoneError] = useState('');
  const [isValidPhone, setIsValidPhone] = useState(false);
  const containerRef = useRef(null);
  const [authAction, setAuthAction] = useState(null); // New state for login/signup choice
  const [loginTab, setLoginTab] = useState('phone'); // 'phone' | 'email'
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const emailInputRef = useRef(null);

  // Handlers for the new auth choice
  const handleLoginClick = () => {
    setAuthAction('login');
  };

  const handleRegisterClick = () => {
    window.open(URLS.REGISTER, '_blank');
  };

  const handleBackClick = () => {
    setAuthAction(null);
    setPhone('');
    setPhoneError('');
    setIsValidPhone(false);
  };

  // Use the PWA install hook
  const { canInstall, installApp, showFallbackInstructions } = usePWAInstall();

  // Enhanced phone validation
  const validatePhone = useCallback((phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/\s/g, '');
    if (cleanPhone.length === 0) {
      setPhoneError('');
      setIsValidPhone(false);
      return;
    }
    if (cleanPhone.length < 10) {
      setPhoneError('Phone number must be 10 digits');
      setIsValidPhone(false);
      return;
    }
    if (cleanPhone.length > 10) {
      setPhoneError('Phone number cannot exceed 10 digits');
      setIsValidPhone(false);
      return;
    }
    if (!/^\d+$/.test(cleanPhone)) {
      setPhoneError('Phone number must contain only digits');
      setIsValidPhone(false);
      return;
    }
    setPhoneError('');
    setIsValidPhone(true);
  }, []);

  // Email validation
  const validateEmail = useCallback((emailValue) => {
    if (emailValue.length === 0) {
      setEmailError('');
      setIsValidEmail(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setEmailError('Please enter a valid email address');
      setIsValidEmail(false);
      return;
    }
    setEmailError('');
    setIsValidEmail(true);
  }, []);

  // Handle phone input with validation
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(value);
    validatePhone(value);
  };

  // Handle email input with validation
  const handleEmailChange = (e) => {
    const value = e.target.value.trim();
    setEmail(value);
    validateEmail(value);
  };

  // Enhanced keyboard visibility detection
  useEffect(() => {
    const initialHeight = window.innerHeight;
    setViewportHeight(initialHeight);

    const handleResize = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const heightDifference = initialHeight - currentHeight;
      const keyboardVisible = heightDifference > 150;
      setIsKeyboardVisible(keyboardVisible);
      setViewportHeight(currentHeight);
    };

    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height;
        const heightDifference = initialHeight - currentHeight;
        const keyboardVisible = heightDifference > 150;
        setIsKeyboardVisible(keyboardVisible);
        setViewportHeight(currentHeight);
      }
    };

    const handleFocus = () => {
      setTimeout(() => {
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const heightDifference = initialHeight - currentHeight;
        if (heightDifference > 150) {
          setIsKeyboardVisible(true);
          setViewportHeight(currentHeight);
          if (inputRef.current) {
            inputRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      }, 300);
    };

    const handleBlur = () => {
      setTimeout(() => {
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const heightDifference = initialHeight - currentHeight;
        if (heightDifference <= 150) {
          setIsKeyboardVisible(false);
          setViewportHeight(initialHeight);
        }
      }, 100);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    }

    window.addEventListener('resize', handleResize);

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);
    }

    const allInputs = document.querySelectorAll('input, textarea');
    allInputs.forEach((input) => {
      input.addEventListener('focus', handleFocus);
      input.addEventListener('blur', handleBlur);
    });

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      }
      window.removeEventListener('resize', handleResize);
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
      allInputs.forEach((input) => {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
      });
    };
  }, []);

  useEffect(() => {
    const displayMode = getPWADisplayMode();
    if (displayMode === 'browser') {
      if (isIOS()) {
        // iOS specific logic
      } else if ('getInstalledRelatedApps' in navigator) {
        navigator.getInstalledRelatedApps().then((apps) => {
          if (apps.length > 0) {
            window.location.replace(window.location.href);
          }
        });
      }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!isValidPhone) {
      toast.error('Please enter a valid 10-digit phone number');
      inputRef.current?.focus();
      return;
    }
    setLoginMethod('phone');
    setPhoneNumber(phone);
    const res = await initiateLogin(phone);
    if (res) {
      toast.success('Verification code sent successfully!');
      navigate('/auth/otp', { replace: true });
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!isValidEmail) {
      toast.error('Please enter a valid email address');
      emailInputRef.current?.focus();
      return;
    }
    setLoginMethod('email');
    setEmailAddress(email);
    const res = await initiateLoginByEmail(email);
    if (res) {
      toast.success('Verification code sent to your email!');
      navigate('/auth/otp', { replace: true });
    }
  };

  const handleInstallPwa = async () => {
    const result = await installApp(() => setIsIosModalOpen(true));
    if (result.success) {
      toast.success(result.message);
    } else if (result.message === 'No install prompt available') {
      const instructions = showFallbackInstructions();
      toast.info(`To install the app: ${instructions}`, { autoClose: 8000 });
    } else if (result.message !== 'iOS install instructions shown') {
      toast.info('For a smoother experience, install the app anytime from your browser settings.');
    }
  };

  useEffect(() => {
    if (state && state?.isEdit) {
      setAuthAction('login'); // Show login form directly if editing number
      setPhone(state?.phoneNumber || '');
      validatePhone(state?.phoneNumber || '');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [state, validatePhone]);

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-gray-50 to-white">
      {/* Loading Overlay */}
      {loading && <BiglocalLoader title="Sending the Verification code..." />}

      {/* Mobile Layout */}
      <div
        className="md:hidden pb-16 w-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-emerald-50 transition-all duration-300"
        ref={containerRef}
        style={{
          height: isKeyboardVisible ? `${viewportHeight}px` : '100vh',
          minHeight: isKeyboardVisible ? `${viewportHeight}px` : '100vh',
          maxHeight: isKeyboardVisible ? `${viewportHeight}px` : 'none',
        }}
      >
        {/* Enhanced Header */}
        <div className="py-4 px-4 pb-2">
          <div className="flex items-center justify-center mb-1">
            <img src="/images/biglocallogo3.webp" alt="Biglocal" className="w-32" />
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center relative">
          <img src="/images/login/banner1.webp" alt="Banner image" />
        </div>

        <div className="px-4 mt-auto pb-4">
          {authAction === 'login' ? (
            <div className="animate-in slide-in-from-right-full duration-300">
              <div className="flex items-center mb-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBackClick}
                  className="mr-2 h-9 w-9"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <h5 className="text-xl text-[#f77c06] font-bold">
                  {loginTab === 'phone' ? 'Enter your phone number' : 'Enter your email'}
                </h5>
              </div>

              {/* Login Method Tabs for Mobile */}
              <div className="flex mb-4 border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setLoginTab('phone')}
                  className={`flex-1 py-2 text-sm font-semibold border-b-2 transition-all ${
                    loginTab === 'phone'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Phone
                </button>
                <button
                  type="button"
                  onClick={() => setLoginTab('email')}
                  className={`flex-1 py-2 text-sm font-semibold border-b-2 transition-all ${
                    loginTab === 'email'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Email
                </button>
              </div>

              {loginTab === 'phone' ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <div className="relative">
                      <div
                        className={`flex items-center border-2 rounded-lg overflow-hidden transition-all duration-200 ${
                          phoneError
                            ? 'border-red-300 bg-red-50'
                            : isValidPhone
                              ? 'border-green-300 bg-green-50'
                              : 'border-gray-300 bg-white hover:border-gray-400 focus-within:border-blue-500'
                        }`}
                      >
                        <div className="flex items-center px-3 py-3 border-r border-gray-300 bg-gray-50">
                          <img src="/images/flag.svg" alt="India flag" className="w-5 h-4 mr-2" />
                        </div>
                        <input
                          ref={inputRef}
                          type="tel"
                          placeholder="Enter your phone number"
                          value={phone}
                          onChange={handlePhoneChange}
                          className="flex-1 px-3 py-3 outline-none text-gray-900 bg-transparent placeholder:text-gray-500"
                          inputMode="numeric"
                          maxLength={10}
                          required
                          aria-describedby={phoneError ? 'phone-error' : 'phone-help'}
                        />
                        {isValidPhone && (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 animate-in zoom-in duration-200" />
                        )}
                        {phoneError && (
                          <AlertCircle className="w-5 h-5 text-red-500 mr-3 animate-in zoom-in duration-200" />
                        )}
                      </div>
                      {phoneError && (
                        <p
                          id="phone-error"
                          className="text-xs text-red-600 mt-1 animate-in slide-in-from-top duration-200"
                        >
                          {phoneError}
                        </p>
                      )}
                    </div>
                    <p id="phone-help" className="text-xs text-gray-500">
                      We&apos;ll send you a verification code on whatsapp to this number
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !isValidPhone}
                    className={`w-full py-3 rounded-full font-medium mb-4 transition-all duration-200 ${
                      isValidPhone
                        ? 'bg-blue-600 hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </div>
                    ) : (
                      'Continue →'
                    )}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <div className="relative">
                      <div
                        className={`flex items-center border-2 rounded-lg overflow-hidden transition-all duration-200 ${
                          emailError
                            ? 'border-red-300 bg-red-50'
                            : isValidEmail
                              ? 'border-green-300 bg-green-50'
                              : 'border-gray-300 bg-white hover:border-gray-400 focus-within:border-blue-500'
                        }`}
                      >
                        <div className="flex items-center px-3 py-3 border-r border-gray-300 bg-gray-50">
                          <Mail className="w-4 h-4 text-gray-500" />
                        </div>
                        <input
                          ref={emailInputRef}
                          type="email"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={handleEmailChange}
                          className="flex-1 px-3 py-3 outline-none text-gray-900 bg-transparent placeholder:text-gray-500"
                          required
                          aria-describedby={emailError ? 'email-error' : 'email-help'}
                        />
                        {isValidEmail && (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 animate-in zoom-in duration-200" />
                        )}
                        {emailError && (
                          <AlertCircle className="w-5 h-5 text-red-500 mr-3 animate-in zoom-in duration-200" />
                        )}
                      </div>
                      {emailError && (
                        <p
                          id="email-error"
                          className="text-xs text-red-600 mt-1 animate-in slide-in-from-top duration-200"
                        >
                          {emailError}
                        </p>
                      )}
                    </div>
                    <p id="email-help" className="text-xs text-gray-500">
                      We&apos;ll send you a verification code to your email
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !isValidEmail}
                    className={`w-full py-3 rounded-full font-medium mb-4 transition-all duration-200 ${
                      isValidEmail
                        ? 'bg-blue-600 hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </div>
                    ) : (
                      'Continue →'
                    )}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <AuthChoice onLogin={handleLoginClick} onRegister={handleRegisterClick} />
          )}
        </div>

        {/* Enhanced Footer */}
        <div className="px-6 pt-2 pb-2 text-sm flex items-center justify-center gap-4 flex-wrap ">
          {[
            { text: 'Terms', action: () => setIsTermsOpen(true) },
            { text: 'Privacy', action: () => setIsPrivacyOpen(true) },
            { text: 'About', action: () => window.open('https://about.biglocal.in/', '_blank') },
            { text: 'Contact', action: () => setIsContactUsOpen(true) },
            ...(!isStandalone() && canInstall
              ? [{ text: 'Install App', action: handleInstallPwa }]
              : []),
          ].map((item, index) => (
            <button
              key={item.text}
              type="button"
              onClick={item.action}
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition-all duration-200 hover:scale-105 active:scale-95"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {item.text}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex w-full">
        <div className="w-1/2 bg-white relative flex flex-col overflow-hidden p-12 text-slate-800">
          {/* <img src="/images/biglocallogo3.webp" alt="Biglocal" className="w-32 z-10" /> */}
          <div className="flex-1 flex items-center justify-center relative">
            <img src="/images/login/banner1.webp" alt="banner image" className="w-3/4" />
          </div>
        </div>

        <div className="w-1/2 bg-white flex items-center justify-center p-12">
          <div className="w-full max-w-md">
            {authAction === 'login' ? (
              <div className="animate-in fade-in duration-300 w-full">
                <div className="flex items-center mb-8 relative">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleBackClick}
                    className="absolute -left-16"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="text-center w-full">
                    <h2 className="text-3xl font-bold text-gray-900">Login to your Account</h2>
                    <p className="text-gray-500 mt-2">Sign in to your seller account.</p>
                  </div>
                </div>

                {/* Login Method Tabs */}
                <div className="flex mb-6 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setLoginTab('phone')}
                    className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-all ${
                      loginTab === 'phone'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Phone Number
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginTab('email')}
                    className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-all ${
                      loginTab === 'email'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Email
                  </button>
                </div>

                {loginTab === 'phone' ? (
                  <form onSubmit={handleLogin} className="space-y-2">
                    <div>
                      <div
                        className={`flex items-center border-2 rounded-lg overflow-hidden transition-all duration-200 ${
                          phoneError
                            ? 'border-red-400'
                            : isValidPhone
                              ? 'border-green-400'
                              : 'border-gray-300 focus-within:border-blue-500'
                        }`}
                      >
                        <div className="flex items-center px-3 py-3 border-r border-gray-300 bg-gray-50 text-sm">
                          <img src="/images/flag.svg" alt="India flag" className="w-5 mr-2" />
                          +91
                        </div>
                        <Input
                          ref={inputRef}
                          type="tel"
                          placeholder="Enter your phone number"
                          value={phone}
                          onChange={handlePhoneChange}
                          className="flex-1 px-3 py-3 border-none focus-visible:ring-offset-0 focus-visible:ring-0 bg-transparent text-base"
                          inputMode="numeric"
                          maxLength={10}
                          required
                        />
                        {isValidPhone && <CheckCircle className="w-5 h-5 text-green-500 mr-3" />}
                        {phoneError && <AlertCircle className="w-5 h-5 text-red-500 mr-3" />}
                      </div>
                      {phoneError && <p className="text-xs text-red-600 mt-1">{phoneError}</p>}
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || !isValidPhone}
                      className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 rounded-full font-bold"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleEmailLogin} className="space-y-2">
                    <div>
                      <div
                        className={`flex items-center border-2 rounded-lg overflow-hidden transition-all duration-200 ${
                          emailError
                            ? 'border-red-400'
                            : isValidEmail
                              ? 'border-green-400'
                              : 'border-gray-300 focus-within:border-blue-500'
                        }`}
                      >
                        <div className="flex items-center px-3 py-3 border-r border-gray-300 bg-gray-50 text-sm">
                          <Mail className="w-4 h-4 text-gray-500" />
                        </div>
                        <Input
                          ref={emailInputRef}
                          type="email"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={handleEmailChange}
                          className="flex-1 px-3 py-3 border-none focus-visible:ring-offset-0 focus-visible:ring-0 bg-transparent text-base"
                          required
                        />
                        {isValidEmail && <CheckCircle className="w-5 h-5 text-green-500 mr-3" />}
                        {emailError && <AlertCircle className="w-5 h-5 text-red-500 mr-3" />}
                      </div>
                      {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || !isValidEmail}
                      className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 rounded-full font-bold"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                    </Button>
                  </form>
                )}

                <div className="mt-6 text-center text-xs text-gray-500">
                  By continuing, you agree to our{' '}
                  <button onClick={() => setIsTermsOpen(true)} className="font-semibold text-blue-600 hover:underline">
                    Terms of Service
                  </button>{' '}
                  and{' '}
                  <button onClick={() => setIsPrivacyOpen(true)} className="font-semibold text-blue-600 hover:underline">
                    Privacy Policy
                  </button>.
                </div>
              </div>
            ) : (
              <AuthChoice onLogin={handleLoginClick} onRegister={handleRegisterClick} />
            )}

            <div className="mt-8 pt-6 border-t flex items-center justify-center gap-4">
              <Button variant="outline" onClick={() => window.open(URLS.ABOUT, '_blank')}>
                <BookOpenText size={16} className="mr-2" />
                About Us
              </Button>
              <Button variant="outline" onClick={() => setIsContactUsOpen(true)}>
                <Mail size={16} className="mr-2" />
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EmailForm isOpen={isContactUsOpen} onClose={() => setIsContactUsOpen(false)} />
      <TermsAndConditionsPopup isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
      <PrivacyPolicyPopup isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
      <NotRegisteredModal
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        phoneNumber={phone}
      />

      {/* iOS Installation Modal */}
      <Dialog open={isIosModalOpen} onOpenChange={setIsIosModalOpen}>
        <DialogContent className=" w-11/12 max-h-screen p-0 overflow-hidden animate-in zoom-in duration-300">
          <img
            src="/iosdemo.webp"
            alt="iOS installation tutorial"
            className="h-auto w-full rounded-lg hover:scale-105 transition-transform duration-300"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

//  Email Form Component
const EmailForm = ({ isOpen, onClose }) => {
  const form = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await emailjs.sendForm(
        import.meta.env?.VITE_EMAILJS_SERVICE_ID,
        import.meta.env?.VITE_EMAILJS_TEMPLATE_ID,
        form.current,
        {
          publicKey: import.meta.env?.VITE_EMAILJS_API_KEY,
        }
      );
      toast.success('Email Sent ✅');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setFormData({ name: '', email: '', message: '' });
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm pt-5 pb-3 rounded-lg animate-in zoom-in duration-300">
        <DialogHeader className="py-0">
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Mail className="mr-2 h-5 w-5 text-green-600" />
              Contact Us
            </span>
            <button
              onClick={() => window.open(`https://wa.me/919899784200`)}
              className="w-10 h-10 rounded-full bg-green-100 hover:bg-green-200 flex items-center justify-center transition-colors duration-200 hover:scale-110"
              aria-label="Contact via WhatsApp"
            >
              <img src="/image.webp" alt="whatsapp" className="w-6 h-6" />
            </button>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={sendEmail} ref={form} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              name="user_name"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`focus-visible:ring-green-500 focus-visible:ring-1 focus-visible:ring-offset-0 transition-colors duration-200 ${
                errors.name ? 'border-red-300' : ''
              }`}
              required
            />
            {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="user_email"
              placeholder="Your email address"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`focus-visible:ring-green-500 focus-visible:ring-offset-0 focus-visible:ring-1 transition-colors duration-200 ${
                errors.email ? 'border-red-300' : ''
              }`}
              required
            />
            {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="message">Message</Label>
            <textarea
              id="message"
              name="message"
              rows={3}
              placeholder="How can we help you?"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              className={`focus-visible:ring-green-500 block border focus-visible:ring-offset-0 focus-visible:ring-1 w-full focus:outline-none rounded-md p-2 transition-colors duration-200 ${
                errors.message ? 'border-red-300' : ''
              }`}
              required
            />
            {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
          </div>
          <div className="flex items-center mt-4 gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="hover:scale-105 active:scale-95 transition-transform duration-200 bg-transparent"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-green-700 hover:bg-green-800 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </div>
              ) : (
                'Send Message'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
